<template>
  <div class="GamChessSetCounter" :class="{ 'is-large': isLarge }">
    <GamButton
      :variant="GamButtonVariant.STEKLO"
      :size="GamButtonSize.X_SMALL"
      :left-icon="GamIconName.MINUS"
      :is-disabled="isDisabled || modelValue <= min"
      is-icon-only
      @click="handleStep(-1)"
    />

    <div class="count-field">
      <span class="count-value">{{ modelValue }}</span>
      <GamIcon :name="GamIconName.CHESS_SET" :size="iconSize" class="count-icon" is-current />
    </div>

    <GamButton
      :variant="GamButtonVariant.STEKLO"
      :size="GamButtonSize.X_SMALL"
      :left-icon="GamIconName.PLUS"
      :is-disabled="isDisabled || modelValue >= max"
      is-icon-only
      @click="handleStep(1)"
    />
  </div>
</template>

<script lang="ts" setup>
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed } from 'vue';

interface GamChessSetCounterProps {
  modelValue: number;
  min?: number;
  max?: number;
  isLarge?: boolean;
  isDisabled?: boolean;
}

const props = withDefaults(defineProps<GamChessSetCounterProps>(), {
  min: 0,
  max: Number.MAX_SAFE_INTEGER,
  isLarge: false,
  isDisabled: false,
});

const emit = defineEmits<(event: 'update:modelValue', value: number) => void>();

const iconSize = computed(() => (props.isLarge ? '18px' : '14px'));

const handleStep = (delta: number) => {
  const next = Math.min(props.max, Math.max(props.min, props.modelValue + delta));

  if (next === props.modelValue) return;

  emit('update:modelValue', next);
};
</script>

<style lang="scss" scoped>
.GamChessSetCounter {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

/* Board glyph sits inside the value field, right of the number, like the walk break CTA. */
.count-field {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  height: 24px;
  padding: 0 0.5rem;
  border-radius: 30px;
  border: 1px solid var(--color-white-10);
  background-color: var(--color-dark-5);
  backdrop-filter: blur(8px);
  color: var(--color-white);
}

.count-value {
  font-size: 14px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.count-icon {
  opacity: 0.7;
}

.is-large {
  gap: 0.5rem;

  .count-field {
    height: 30px;
    padding: 0 0.75rem;
    gap: 0.375rem;
  }

  .count-value {
    font-size: 20px;
    font-weight: 700;
  }
}
</style>
